import React, { useContext, useEffect, useState } from 'react'
import { Button, Grid, TextField, Typography } from '@mui/material'
import API from '../../services/API'
import { GlobalContext } from '../../App'
import AddressAutocomplete from '../patientTransport/components/AddressAutocomplete'

// Create / edit form for a base location. Moved here from Site Configuration
// when base management became part of the Base Locations dashboard.
//   existing  — base record to edit (omit to create)
//   onClose   — called on cancel
//   onSaved   — called with the API response after a successful save
const BaseLocationForm = ({ existing, onClose, onSaved }) => {
  const [name, setName] = useState(existing?.name ?? '')
  const [duplicateNameError, setDuplicateNameError] = useState(false)
  const [address, setAddress] = useState(existing?.address ?? {})
  const [location, setLocation] = useState(existing?.location)
  const [addressMeta, setAddressMeta] = useState(existing?.addressMeta)
  const [onCallNumber, setOnCallNumber] = useState(existing?.onCallNumber ?? '')
  const [saving, setSaving] = useState(false)
  const { setToast } = useContext(GlobalContext)

  useEffect(() => {
    // onCallNumber is select:false on the schema, so records seeded from the
    // options list don't carry it — fetch the full record to prefill it.
    if (!existing?._id || existing.onCallNumber !== undefined) return
    API.get(`/base-location/${existing._id}`).then((res) => {
      if (res.status === 200) setOnCallNumber(res.data?.onCallNumber ?? '')
    })
  }, [existing?._id])

  const handleSubmit = (e) => {
    e.preventDefault()
    setSaving(true)
    API.post(existing ? '/edit-base-location' : '/new-base-location', {
      name,
      _id: existing?._id,
      address,
      location,
      addressMeta,
      onCallNumber,
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({
            message: existing ? 'Location Updated' : 'New Location Added',
          })
          onSaved?.(res.data)
        } else if (res.data?.error === 'BaseLocation name must be unique.') {
          setDuplicateNameError(true)
        } else {
          setToast({
            message: 'Unable to save location. Please try again later',
            severity: 'error',
          })
        }
      })
      .finally(() => setSaving(false))
  }

  return (
    <Grid container spacing={2} component="form" onSubmit={handleSubmit}>
      <Grid item xs={12}>
        <Typography variant="title3">
          {existing ? 'Edit Base Location' : 'Add a New Base Location'}
        </Typography>
      </Grid>
      <Grid item xs={12} container spacing={1}>
        <Grid item xs={12}>
          <TextField
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              setDuplicateNameError(false)
            }}
            label="Location Name"
            fullWidth
            required
            autoComplete="off"
            error={duplicateNameError}
            helperText={
              duplicateNameError && 'A location with this name already exists'
            }
          />
        </Grid>
        <Grid item xs={12}>
          <AddressAutocomplete
            label="Find address or postcode"
            address={{
              addressLine1: address?.lineOne,
              addressLine2: address?.lineTwo,
              town: address?.town,
              postCode: address?.postCode,
              location,
            }}
            onSelect={(option) => {
              setAddress((prev) => ({
                ...prev,
                lineOne: option.addressLine1 || option.name || '',
                lineTwo: option.addressLine2 || '',
                town: option.town || option.city || '',
                postCode: option.postCode || '',
              }))
              // Geocoder options carry a raw coordinates pair, not a GeoJSON
              // location — build the point here or the base saves without one
              // and base-to-base mileage can't price.
              setLocation(
                Array.isArray(option.coordinates) &&
                  option.coordinates.length === 2
                  ? { type: 'Point', coordinates: option.coordinates }
                  : option.location || undefined,
              )
              setAddressMeta(option.addressMeta || undefined)
            }}
            helperText={
              location?.coordinates?.length === 2
                ? undefined
                : 'Selecting an address sets a map point for this base — needed if any tariff charges mileage base-to-base.'
            }
          />
        </Grid>
        {[
          ['lineOne', 'Address Line One'],
          ['lineTwo', 'Address Line Two'],
          ['town', 'Town'],
          ['county', 'County'],
          ['postCode', 'Post Code'],
        ].map(([field, label]) => (
          <Grid item xs={12} lg={6} key={field}>
            <TextField
              value={address?.[field] ?? ''}
              onChange={(e) =>
                setAddress((prev) => ({ ...prev, [field]: e.target.value }))
              }
              label={label}
              fullWidth
              autoComplete="off"
              size="small"
            />
          </Grid>
        ))}
        <Grid item xs={12}>
          <TextField
            value={onCallNumber}
            onChange={(e) => setOnCallNumber(e.target.value)}
            label="On-Call Number"
            helperText="Shared standby line for this site. If set, the home screen On Call card will call this number instead of the assigned staff member's personal mobile."
            fullWidth
            autoComplete="off"
            size="small"
          />
        </Grid>
        <Grid item container spacing={2}>
          <Grid item xs={12} md={6}>
            <Button
              variant="outlined"
              color="error"
              type="button"
              fullWidth
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item xs={12} md={6}>
            <Button
              variant="contained"
              color="success"
              type="submit"
              fullWidth
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Submit'}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default BaseLocationForm
